/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {MouseEvent} from 'react';
import {GenericObject} from '@ion/core/src/types/entities';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {FormState} from './useFormState';

export type UseFormWithValidationParams = {
  serialize: (state: FormState) => GenericObject;
  validate?: GenericObject;
  onValidationSuccess: (serializedFormState: GenericObject) => void;
  onValidationFailure: (errors: FormState) => void;
};

interface UseFormWithValidationResult {
  onSubmit: (currentFormState: FormState, e: MouseEvent) => void;
}

/**
 * @useFormWithValidation
 *
 * Provides a re-usable form submission handler with callback options for validation success / failure.
 *
 * ```typescript
 *
 *   import {useFormWithValidation} from '@ion/dls/src/hooks/';
 *
 *   const {onSubmit} = useFormWithValidation({
 *     serialize,
 *     validate,
 *     onValidationSuccess,
 *     onValidationFailure,
 *   });
 *
 * ```
 *
 */
const useFormWithValidation = ({
  validate,
  serialize,
  onValidationSuccess,
  onValidationFailure
}: UseFormWithValidationParams): UseFormWithValidationResult => {
  const onSubmit = (currentFormState: FormState, e?: MouseEvent) => {
    e?.preventDefault();

    const errors = {};
    for (const [name, val] of Object.entries(currentFormState)) {
      if ((val?.required && isEmpty(val.value)) || val?.error ||
      (validate?.[name] && !validate?.[name]?.(val))) {
        errors[name] = {...val, error: true};
      }
    }
    if (isObjectEmpty(errors)) {
      const serialized = serialize(currentFormState);
      onValidationSuccess?.(serialized);
    } else {
      onValidationFailure?.(errors);
    }
  };

  return {
    onSubmit
  };
};

export default useFormWithValidation;
